import React, { useState, useEffect } from 'react';
import { BookOpen, Award, CheckCircle, XCircle, Clock, ArrowRight, User, CreditCard, Home, Loader, Timer, TrendingUp, Target, Zap, Filter } from 'lucide-react';
import PaymentModal from './components/PaymentModal';

// ========================================
// API CONFIGURATION
// ========================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// ========================================
// PIE CHART COMPONENT
// ========================================
const PieChart = ({ correct, total }) => {
  const incorrect = total - correct;
  const correctPercentage = (correct / total) * 100;
  const incorrectPercentage = (incorrect / total) * 100;
  
  // Calculate SVG path for pie slices
  const radius = 120;
  const circumference = 2 * Math.PI * radius;
  
  // Correct slice starts at top (0 degrees) and goes clockwise
  const correctDashArray = `${(correctPercentage / 100) * circumference} ${circumference}`;
  const correctDashOffset = 0;
  
  // Incorrect slice follows the correct slice
  const incorrectDashArray = `${(incorrectPercentage / 100) * circumference} ${circumference}`;
  const incorrectDashOffset = -((correctPercentage / 100) * circumference);
  
  return (
    <div className="flex flex-col items-center">
      <svg width="300" height="300" viewBox="0 0 300 300" className="mb-6">
        {/* Background circle */}
        <circle
          cx="150"
          cy="150"
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="60"
        />
        
        {/* Correct answers slice (green) */}
        <circle
          cx="150"
          cy="150"
          r={radius}
          fill="none"
          stroke="#10b981"
          strokeWidth="60"
          strokeDasharray={correctDashArray}
          strokeDashoffset={correctDashOffset}
          transform="rotate(-90 150 150)"
          className="transition-all duration-500"
        />
        
        {/* Incorrect answers slice (red) */}
        <circle
          cx="150"
          cy="150"
          r={radius}
          fill="none"
          stroke="#ef4444"
          strokeWidth="60"
          strokeDasharray={incorrectDashArray}
          strokeDashoffset={incorrectDashOffset}
          transform="rotate(-90 150 150)"
          className="transition-all duration-500"
        />
        
        {/* Center text showing percentage */}
        <text
          x="150"
          y="140"
          textAnchor="middle"
          fontSize="52"
          fontWeight="bold"
          fill="#1f2937"
        >
          {correctPercentage.toFixed(0)}%
        </text>
        <text
          x="150"
          y="175"
          textAnchor="middle"
          fontSize="20"
          fill="#6b7280"
        >
          Correct
        </text>
      </svg>
      
      {/* Legend */}
      <div className="flex gap-8 text-base">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 bg-emerald-500 rounded"></div>
          <span className="text-gray-700">
            <span className="font-bold text-lg">{correct}</span> Correct
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 bg-red-500 rounded"></div>
          <span className="text-gray-700">
            <span className="font-bold text-lg">{incorrect}</span> Incorrect
          </span>
        </div>
      </div>
    </div>
  );
};

// ========================================
// EXAM HISTORY COMPONENT
// ========================================
const ExamHistory = ({ userId, token }) => {
  const [examHistory, setExamHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExamHistory = async () => {
      if (!userId || !token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/progress/history`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Sort by most recent first
          const sortedHistory = (data.history || []).sort((a, b) => 
            new Date(b.completed_at) - new Date(a.completed_at)
          );
          setExamHistory(sortedHistory.slice(0, 10)); // Show last 10 attempts
        } else {
          setError('Failed to load exam history');
        }
      } catch (err) {
        console.error('Error fetching exam history:', err);
        setError('Failed to load exam history');
      } finally {
        setLoading(false);
      }
    };

    fetchExamHistory();
  }, [userId, token]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExamTypeBadge = (examType) => {
    if (examType === 'foundation') {
      return 'bg-blue-100 text-blue-700';
    }
    return 'bg-indigo-100 text-indigo-700';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading exam history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Award className="w-10 h-10 text-gray-400" />
        </div>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  if (examHistory.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Award className="w-10 h-10 text-gray-400" />
        </div>
        <p className="text-gray-600 mb-4">No exam attempts yet</p>
        <p className="text-gray-500 text-sm">
          Start practicing to track your progress
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {examHistory.map((attempt, index) => {
        const percentage = ((attempt.score / attempt.total_questions) * 100).toFixed(1);
        const passingPercentage = attempt.exam_type === 'foundation' ? 60 : 55;
        const passed = parseFloat(percentage) >= passingPercentage;

        return (
          <div
            key={attempt.id || index}
            className="bg-gradient-to-r from-gray-50 to-indigo-50 p-6 rounded-xl hover:shadow-md transition-all border border-gray-200"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className={`p-3 rounded-xl ${passed ? 'bg-emerald-100' : 'bg-orange-100'}`}>
                  {passed ? (
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <Target className="w-6 h-6 text-orange-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-900 capitalize">
                      {attempt.exam_type} - Exam {attempt.exam_number}
                    </h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getExamTypeBadge(attempt.exam_type)}`}>
                      {attempt.exam_type === 'foundation' ? 'Foundation' : 'Practitioner'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Completed on {formatDate(attempt.completed_at)}
                  </p>
                  <div className="flex items-center gap-3">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      passed
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {passed ? '✓ Passed' : '○ Practice'}
                    </span>
                    <span className="text-sm text-gray-600">
                      {attempt.score} / {attempt.total_questions} correct
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-900">
                  {percentage}%
                </p>
                <p className="text-xs text-gray-500">
                  Pass: {passingPercentage}%
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ========================================
// MAIN APP COMPONENT
// ========================================
function App() {
  // ... rest of your App component code
  // ========================================
  // STATE MANAGEMENT
  // ========================================
  
  // Screen navigation
  const [currentScreen, setCurrentScreen] = useState('home');
  
  // User authentication
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  
  // Exam state
  const [selectedExamType, setSelectedExamType] = useState(null);
  const [selectedExamNumber, setSelectedExamNumber] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [examStartTime, setExamStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [resultsFilter, setResultsFilter] = useState('all'); // 'all', 'correct', 'incorrect'
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [purchasingCourseType, setPurchasingCourseType] = useState(null);

  // Account screen state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ========================================
  // LOAD USER DATA ON APP START
  // ========================================
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // ========================================
  // FETCH PURCHASE HISTORY WHEN VIEWING ACCOUNT
  // ========================================
  useEffect(() => {
    const fetchPurchaseHistory = async () => {
      if (!user || currentScreen !== 'account') return;
      
      setLoadingHistory(true);
      try {
        const data = await apiRequest('/user/purchase-history');
        setPurchaseHistory(data.purchases || []);
      } catch (error) {
        console.error('Failed to load purchase history:', error);
        // If endpoint fails, use mock data based on current access
        const mockHistory = [];
        if (user.hasPaidFoundation) {
          mockHistory.push({
            id: 1,
            courseType: 'foundation',
            courseName: 'PRINCE2 Foundation',
            amount: 21.99,
            currency: 'EUR',
            purchaseDate: new Date().toISOString(),
            status: 'completed'
          });
        }
        if (user.hasPaidPractitioner) {
          mockHistory.push({
            id: 2,
            courseType: 'practitioner',
            courseName: 'PRINCE2 Practitioner',
            amount: 21.99,
            currency: 'EUR',
            purchaseDate: new Date().toISOString(),
            status: 'completed'
          });
        }
        setPurchaseHistory(mockHistory);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchPurchaseHistory();
  }, [user, currentScreen, token]);

  // ========================================
  // INITIALIZE EDIT FIELDS WHEN ENTERING EDIT MODE
  // ========================================
  useEffect(() => {
    if (isEditing && user) {
      setEditName(user.name || '');
      setEditEmail(user.email || '');
    }
  }, [isEditing, user]);

  // ========================================
  // TIMER EFFECT FOR DEMO EXAMS
  // ========================================
  useEffect(() => {
    if (isDemoMode && timeRemaining !== null && timeRemaining > 0 && currentScreen === 'exam') {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isDemoMode, timeRemaining, currentScreen]);

  // ========================================
  // FORMAT TIME
  // ========================================
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ========================================
  // API HELPER FUNCTIONS
  // ========================================
  
  const apiRequest = async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'An error occurred');
    }

    return data;
  };

  // ========================================
  // AUTHENTICATION FUNCTIONS
  // ========================================
  
  const handleRegister = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsLoading(true);

    try {
      const data = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, name }),
      });

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      setToken(data.token);
      setUser(data.user);
      
      setEmail('');
      setPassword('');
      setName('');
      
      setCurrentScreen('home');
    } catch (error) {
      setFormError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsLoading(true);

    try {
      const data = await apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      setToken(data.token);
      setUser(data.user);
      
      setEmail('');
      setPassword('');
      
      setCurrentScreen('home');
    } catch (error) {
      setFormError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setCurrentScreen('home');
  };

  // ========================================
  // PAYMENT FUNCTIONS
  // ========================================
  
  const handleOpenPaymentModal = (courseType) => {
    if (!user) {
      alert('Please log in or create an account first');
      setCurrentScreen('login');
      return;
    }
    
    if (courseType === 'foundation' && user.hasPaidFoundation) {
      alert('You already have access to Foundation exams!');
      return;
    }
    if (courseType === 'practitioner' && user.hasPaidPractitioner) {
      alert('You already have access to Practitioner exams!');
      return;
    }
    
    setPurchasingCourseType(courseType);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (paymentData) => {
    const updatedUser = {
      ...user,
      hasPaidFoundation: paymentData.courseType === 'foundation' ? true : (user.hasPaidFoundation || false),
      hasPaidPractitioner: paymentData.courseType === 'practitioner' ? true : (user.hasPaidPractitioner || false)
    };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    setShowPaymentModal(false);
    setPurchasingCourseType(null);
    
    const courseName = paymentData.courseType === 'foundation' ? 'Foundation' : 'Practitioner';
    alert(`🎉 Payment successful! You now have access to ${courseName} exams.`);
    
    setCurrentScreen('home');
  };

  const goToPayment = (courseType) => {
    if (!user) {
      setCurrentScreen('register');
    } else {
      handleOpenPaymentModal(courseType);
    }
  };

  // ========================================
  // EXAM FUNCTIONS
  // ========================================
  
  const fetchDemoQuestions = async (examType) => {
    setIsLoadingQuestions(true);
    try {
      const response = await fetch(`${API_BASE_URL}/questions/demo/${examType}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load demo questions');
      }
      
      setCurrentQuestions(data.questions);
      return data.questions;
    } catch (error) {
      alert('Failed to load demo questions: ' + error.message);
      return [];
    } finally {
      setIsLoadingQuestions(false);
    }
  };
  
  const fetchQuestions = async (examType, examNumber) => {
    setIsLoadingQuestions(true);
    try {
      const data = await apiRequest(`/questions/${examType}/${examNumber}`);
      setCurrentQuestions(data.questions);
      return data.questions;
    } catch (error) {
      alert('Failed to load questions: ' + error.message);
      return [];
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const startDemoExam = async (examType) => {
    setIsDemoMode(true);
    setSelectedExamType(examType);
    setSelectedExamNumber(0);
    setCurrentScreen('exam');
    
    const questions = await fetchDemoQuestions(examType);
    
    if (questions.length === 0) {
      alert('Demo questions are not available yet. Please try again later or contact support.');
      setCurrentScreen('home');
      return;
    }
    
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setExamStartTime(Date.now());
    setTimeRemaining(15 * 60);
    setShowResults(false);
  };

  const startExam = async (examType, examNumber) => {
    if (!user) {
      alert('Please create an account to take exams');
      setCurrentScreen('register');
      return;
    }

    const hasCourseAccess = examType === 'foundation' ? user.hasPaidFoundation : user.hasPaidPractitioner;
    if (!hasCourseAccess) {
      const courseName = examType === 'foundation' ? 'Foundation' : 'Practitioner';
      alert(`Please purchase ${courseName} access (€21.99) to take ${courseName} exams`);
      goToPayment(examType);
      return;
    }

    setIsDemoMode(false);
    setSelectedExamType(examType);
    setSelectedExamNumber(examNumber);
    setCurrentScreen('exam');

    const questions = await fetchQuestions(examType, examNumber);
    
    if (questions.length === 0) {
      alert('No questions available for this exam yet. Please contact support or try another exam.');
      setCurrentScreen('exam-select');
      return;
    }

    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setExamStartTime(Date.now());
    setTimeRemaining(null);
    setShowResults(false);
  };

  const handleAnswerSelect = (answerIndex) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setUserAnswers(newAnswers);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const navigateToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  const handleSubmitExam = async () => {
    // For backward compatibility, calculate score locally first
    let score = 0;
    userAnswers.forEach((answer, index) => {
      if (answer === currentQuestions[index].correct) {
        score++;
      }
    });

    const timeTaken = Math.floor((Date.now() - examStartTime) / 1000);

    // Try to submit to backend for detailed results
    if (user) {
      try {
        // Convert userAnswers array to object with question IDs as keys
        const answersObj = {};
        currentQuestions.forEach((question, index) => {
          answersObj[question.id] = userAnswers[index];
        });

        const examNum = isDemoMode ? 0 : selectedExamNumber;
        const response = await fetch(
          `${API_BASE_URL}/exams/submit/${selectedExamType}/${examNum}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ answers: answersObj })
          }
        );

        if (response.ok) {
          const detailedResults = await response.json();
          // Store detailed results in questions for the results screen
          setCurrentQuestions(prev => prev.map((q, i) => ({
            ...q,
            detailedResult: detailedResults.detailedResults[i]
          })));
        }
      } catch (error) {
        console.error('Failed to get detailed results:', error);
        // Continue anyway - results will show without explanations
      }
    }

    // Also save to old progress endpoint if not demo
    if (!isDemoMode && user) {
      try {
        await apiRequest('/progress/exam', {
          method: 'POST',
          body: JSON.stringify({
            examType: selectedExamType,
            examNumber: selectedExamNumber,
            score,
            totalQuestions: currentQuestions.length,
            answers: userAnswers,
            timeTaken,
            completedAt: new Date().toISOString(),
          }),
        });
      } catch (error) {
        console.error('Failed to save exam results:', error);
      }
    }

    setShowResults(true);
    setCurrentScreen('results');
    setResultsFilter('all');
  };

  const calculateScore = () => {
    let score = 0;
    userAnswers.forEach((answer, index) => {
      if (answer === currentQuestions[index].correct) {
        score++;
      }
    });
    return score;
  };

  const handleBackToHome = () => {
    setCurrentScreen('home');
    setSelectedExamType(null);
    setSelectedExamNumber(null);
    setIsDemoMode(false);
    setCurrentQuestions([]);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setShowResults(false);
    setTimeRemaining(null);
    setResultsFilter('all');
  };

  // ========================================
  // ACCOUNT MANAGEMENT FUNCTIONS
  // ========================================
  
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdateError('');
    setUpdateSuccess(false);
    setIsUpdating(true);

    try {
      const data = await apiRequest('/user/profile', {
        method: 'PUT',
        body: JSON.stringify({ name: editName, email: editEmail }),
      });

      const updatedUser = { ...user, name: editName, email: editEmail };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setUpdateSuccess(true);
      setIsEditing(false);
      
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (error) {
      setUpdateError(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditName(user?.name || '');
    setEditEmail(user?.email || '');
    setUpdateError('');
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // ========================================
  // RENDER FUNCTIONS
  // ========================================

  /**
   * HOME SCREEN
   */
  const renderHome = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Navigation Bar */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-2 rounded-xl">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                ExamReadyPM
              </h1>
            </div>
            
            {user ? (
              <div className="flex items-center gap-4">
                <div className="hidden md:block text-right">
                  <p className="text-sm text-gray-600">Welcome back,</p>
                  <p className="font-semibold text-gray-900">{user.name}</p>
                  {(user.hasPaidFoundation || user.hasPaidPractitioner) && (
                    <div className="flex items-center gap-1 text-xs text-emerald-600 mt-0.5">
                      <CheckCircle className="w-3 h-3" />
                      <span>
                        {user.hasPaidFoundation && user.hasPaidPractitioner ? 'Full Access' : 
                         user.hasPaidFoundation ? 'Foundation' : 'Practitioner'}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setCurrentScreen('account')}
                  className="px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-xl hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 flex items-center gap-2 font-medium border border-blue-200"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Account</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => setCurrentScreen('login')}
                  className="px-5 py-2.5 bg-white text-blue-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-semibold border border-gray-200"
                >
                  Login
                </button>
                <button
                  onClick={() => setCurrentScreen('register')}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold shadow-lg shadow-blue-500/30"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Zap className="w-4 h-4" />
            PRINCE2 Certification Prep
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent leading-tight">
            Master Your PRINCE2<br />Certification Exam
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Prepare with confidence using our comprehensive practice exams designed to match the real certification experience
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-700 mb-1">480+</div>
              <div className="text-sm text-gray-600">Practice Questions</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-indigo-700 mb-1">8</div>
              <div className="text-sm text-gray-600">Full Length Exams</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-teal-700 mb-1">€21.99</div>
              <div className="text-sm text-gray-600">Per Course</div>
            </div>
          </div>
          
          {user && (!user.hasPaidFoundation || !user.hasPaidPractitioner) && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-blue-700" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    Unlock Your Full Potential
                  </h3>
                  <p className="text-gray-700 text-sm">
                    Get complete access to 4 full-length practice exams for just €21.99 per course
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Exam Cards */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Foundation Card */}
          <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-2xl">
                      <BookOpen className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold">Foundation</h3>
                      <p className="text-blue-100 text-sm">Entry Level</p>
                    </div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm px-5 py-2.5 rounded-xl">
                    <p className="text-2xl font-bold">€21.99</p>
                  </div>
                </div>
                <p className="text-blue-50 leading-relaxed">
                  Build a strong foundation in PRINCE2 project management methodology
                </p>
              </div>
            </div>

            <div className="p-8">
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-medium">4 Full Practice Exams</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-medium">240 Realistic Questions</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-medium">Instant Results & Analytics</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-medium">Lifetime Access</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => startDemoExam('foundation')}
                  className="w-full py-4 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-all duration-200 font-semibold flex items-center justify-center gap-2 border-2 border-blue-200"
                >
                  <Zap className="w-5 h-5" />
                  Try Free Demo (10 Questions)
                </button>
                <button
                  onClick={() => {
                    if (user && user.hasPaidFoundation) {
                      setSelectedExamType('foundation');
                      setCurrentScreen('exam-select');
                    } else {
                      goToPayment('foundation');
                    }
                  }}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center justify-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  {user && user.hasPaidFoundation ? (
                    <>
                      Start Practice Exam
                      <ArrowRight className="w-5 h-5" />
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Get Foundation Access
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Practitioner Card */}
          <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-br from-indigo-600 to-purple-600 p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-2xl">
                      <Award className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold">Practitioner</h3>
                      <p className="text-indigo-100 text-sm">Advanced Level</p>
                    </div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm px-5 py-2.5 rounded-xl">
                    <p className="text-2xl font-bold">€21.99</p>
                  </div>
                </div>
                <p className="text-indigo-50 leading-relaxed">
                  Master advanced scenarios and real-world project management challenges
                </p>
              </div>
            </div>

            <div className="p-8">
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-medium">4 Full Practice Exams</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-medium">240 Advanced Questions</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-medium">Detailed Performance Analytics</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-medium">Lifetime Access</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => startDemoExam('practitioner')}
                  className="w-full py-4 bg-purple-50 text-purple-700 rounded-xl hover:bg-purple-100 transition-all duration-200 font-semibold flex items-center justify-center gap-2 border-2 border-purple-200"
                >
                  <Zap className="w-5 h-5" />
                  Try Free Demo (10 Questions)
                </button>
                <button
                  onClick={() => {
                    if (user && user.hasPaidPractitioner) {
                      setSelectedExamType('practitioner');
                      setCurrentScreen('exam-select');
                    } else {
                      goToPayment('practitioner');
                    }
                  }}
                  className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30"
                >
                  {user && user.hasPaidPractitioner ? (
                    <>
                      Start Practice Exam
                      <ArrowRight className="w-5 h-5" />
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Get Practitioner Access
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-8 bg-white rounded-2xl px-8 py-6 shadow-lg">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              <span className="text-gray-700 font-medium">Instant Access</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              <span className="text-gray-700 font-medium">Secure Payment</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              <span className="text-gray-700 font-medium">Lifetime Updates</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            {/* Contact Information */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">Contact Information</h3>
              <div className="space-y-0.5 text-xs text-gray-600">
                <p className="font-semibold text-gray-900">ExamReadyPM Group</p>
                <p>Herengracht 208, 1016 BS Amsterdam, The Netherlands</p>
                <p>
                  <a 
                    href="mailto:examreadypm@gmail.com" 
                    className="text-blue-600 hover:text-blue-700 transition-colors font-medium"
                  >
                    examreadypm@gmail.com
                  </a>
                </p>
              </div>
            </div>

            {/* Policy Links - Right Side */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6">
              <button 
                onClick={() => setCurrentScreen('terms')}
                className="text-xs text-gray-600 hover:text-blue-700 transition-colors font-medium whitespace-nowrap"
              >
                Terms and Conditions
              </button>
              <button 
                onClick={() => setCurrentScreen('privacy')}
                className="text-xs text-gray-600 hover:text-blue-700 transition-colors font-medium whitespace-nowrap"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setCurrentScreen('complaints')}
                className="text-xs text-gray-600 hover:text-blue-700 transition-colors font-medium whitespace-nowrap"
              >
                Complaint Policy
              </button>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                © {new Date().getFullYear()} ExamReadyPM
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );

  /**
   * EXAM SELECTION SCREEN
   */
  const renderExamSelect = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={handleBackToHome}
          className="mb-8 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium"
        >
          <Home className="w-5 h-5" />
          Back to Home
        </button>

        <div className="mb-12">
          <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent capitalize">
            {selectedExamType} Practice Exams
          </h2>
          <p className="text-gray-600 text-lg">Choose an exam to begin your practice session</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((examNum) => (
            <button
              key={examNum}
              onClick={() => startExam(selectedExamType, examNum)}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-left group border border-gray-100 hover:border-blue-200"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors">
                    Practice Exam {examNum}
                  </h3>
                  <p className="text-gray-600">Full-length simulation</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-xl group-hover:bg-blue-200 transition-colors">
                  <ArrowRight className="w-6 h-6 text-blue-700" />
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-700">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span>60 Questions</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span>Multiple Choice Format</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Target className="w-5 h-5 text-indigo-600" />
                  <span>Realistic Exam Simulation</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Ready to start?</span>
                  <span className="text-blue-700 font-semibold group-hover:translate-x-1 transition-transform">
                    Begin Exam →
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  /**
   * EXAM SCREEN
   */
  const renderExam = () => {
    if (isLoadingQuestions) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
            <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
            <p className="text-2xl font-semibold text-gray-800 mb-2">Loading Your Exam</p>
            <p className="text-gray-600">Preparing questions...</p>
          </div>
        </div>
      );
    }

    if (currentQuestions.length === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-8">
          <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-md text-center">
            <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <p className="text-xl font-semibold text-gray-800 mb-4">No Questions Available</p>
            <p className="text-gray-600 mb-6">This exam is currently unavailable. Please try another exam or contact support.</p>
            <button
              onClick={handleBackToHome}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold"
            >
              Back to Home
            </button>
          </div>
        </div>
      );
    }

    const currentQuestion = currentQuestions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / currentQuestions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Exam Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40 shadow-sm">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 capitalize">
                  {isDemoMode ? `${selectedExamType} - Free Demo` : `${selectedExamType} - Exam ${selectedExamNumber}`}
                </h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  Question {currentQuestionIndex + 1} of {currentQuestions.length}
                </p>
              </div>
              
              {isDemoMode && timeRemaining !== null && (
                <div className={`flex items-center gap-3 px-5 py-3 rounded-xl font-bold text-lg ${
                  timeRemaining < 60 
                    ? 'bg-red-100 text-red-700 border-2 border-red-300' 
                    : timeRemaining < 300
                    ? 'bg-orange-100 text-orange-700 border-2 border-orange-300'
                    : 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                }`}>
                  <Timer className="w-6 h-6" />
                  {formatTime(timeRemaining)}
                </div>
              )}
            </div>
            
            {/* Progress Bar */}
            <div className="relative w-full bg-gray-200 rounded-full h-2.5 overflow-hidden mb-4">
              <div
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Question Navigation Bar */}
            <div className="overflow-x-auto">
              <div className="flex gap-2 min-w-max py-2">
                {currentQuestions.map((_, index) => {
                  const isAnswered = userAnswers[index] !== undefined;
                  const isCurrent = index === currentQuestionIndex;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => navigateToQuestion(index)}
                      className={`w-10 h-10 rounded-lg font-semibold text-sm transition-all duration-200 shrink-0 ${
                        isCurrent
                          ? 'bg-blue-700 text-white shadow-lg scale-110 border-2 border-blue-800'
                          : isAnswered
                          ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      title={`Question ${index + 1}${isAnswered ? ' (Answered)' : ''}`}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
            <div className="flex items-start gap-4 mb-8">
              <div className="bg-blue-100 px-4 py-2 rounded-xl font-bold text-blue-700 text-lg shrink-0">
                Q{currentQuestionIndex + 1}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 leading-relaxed">
                {currentQuestion.question}
              </h3>
            </div>

            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const isSelected = userAnswers[currentQuestionIndex] === index;
                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-600 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold shrink-0 ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </div>
                      <span className="text-gray-800 leading-relaxed">{option}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center gap-4">
            <div className="flex gap-3">
              <button
                onClick={handlePreviousQuestion}
                disabled={currentQuestionIndex === 0}
                className="px-6 py-3 bg-white text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-semibold disabled:opacity-40 disabled:cursor-not-allowed border border-gray-200"
              >
                Previous
              </button>
              
              {isDemoMode && (
                <button
                  onClick={handleBackToHome}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium border border-gray-300"
                >
                  <Home className="w-4 h-4" />
                  <span className="hidden sm:inline">Exit Demo</span>
                </button>
              )}
            </div>

            {currentQuestionIndex === currentQuestions.length - 1 ? (
              <button
                onClick={handleSubmitExam}
                className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:from-emerald-700 hover:to-green-700 transition-all duration-200 font-semibold shadow-lg shadow-emerald-500/30 flex items-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Submit Exam
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold shadow-lg shadow-blue-500/30 flex items-center gap-2"
              >
                Next Question
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /**
   * RESULTS SCREEN
   */
  const renderResults = () => {
    const score = calculateScore();
    const percentage = ((score / currentQuestions.length) * 100).toFixed(1);
    const passingPercentage = selectedExamType === 'foundation' ? 60 : 55;
    const passed = percentage >= passingPercentage;

    // Filter questions based on selected filter
    const getFilteredQuestions = () => {
      return currentQuestions.filter((question, index) => {
        const userAnswer = userAnswers[index];
        const isCorrect = userAnswer === question.correct;
        
        if (resultsFilter === 'correct') return isCorrect;
        if (resultsFilter === 'incorrect') return !isCorrect;
        return true; // 'all'
      }).map((question, filteredIndex) => {
        // Find original index
        const originalIndex = currentQuestions.indexOf(question);
        return { question, originalIndex };
      });
    };

    const filteredQuestions = getFilteredQuestions();
    const correctCount = currentQuestions.filter((q, i) => userAnswers[i] === q.correct).length;
    const incorrectCount = currentQuestions.length - correctCount;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
        <div className="max-w-5xl mx-auto">
          {/* Results Summary Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8">
            {/* Header with gradient */}
            <div className={`${passed ? 'bg-gradient-to-r from-emerald-500 to-green-600' : 'bg-gradient-to-r from-blue-500 to-indigo-600'} p-8 text-white relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
              
              {isDemoMode && (
                <div className="relative z-10 text-center mb-4">
                  <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-bold">
                    FREE DEMO COMPLETED
                  </span>
                </div>
              )}
              
              <div className="relative z-10 text-center">
                <div className={`inline-block p-4 rounded-full mb-6 ${passed ? 'bg-white/20' : 'bg-white/20'} backdrop-blur-sm`}>
                  {passed ? (
                    <CheckCircle className="w-20 h-20" />
                  ) : (
                    <Target className="w-20 h-20" />
                  )}
                </div>
                <h2 className="text-5xl font-bold mb-3">
                  {passed ? 'Excellent Work!' : 'Keep Practicing!'}
                </h2>
                <p className="text-2xl mb-2 text-white/90">
                  {score} out of {currentQuestions.length} correct
                </p>
                <p className="text-white/80">
                  Pass rate: {passingPercentage}% • You scored: {percentage}%
                </p>
              </div>
            </div>

            {/* Score Breakdown with Pie Chart */}
            <div className="p-8">
              {/* Pie Chart */}
              <div className="mb-8 flex justify-center">
                <PieChart correct={score} total={currentQuestions.length} />
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-600">Correct Answers</p>
                    <div className="bg-emerald-100 p-2 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-4xl font-bold text-emerald-600">{score}</p>
                </div>
                
                <div className="bg-white p-6 rounded-2xl shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-600">Incorrect Answers</p>
                    <div className="bg-red-100 p-2 rounded-lg">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                  </div>
                  <p className="text-4xl font-bold text-red-600">
                    {currentQuestions.length - score}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-2xl shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-600">Your Score</p>
                    <div className="bg-gray-100 p-2 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-gray-700" />
                    </div>
                  </div>
                  <p className="text-4xl font-bold text-gray-900">{percentage}%</p>
                </div>
              </div>

              {/* Action Buttons */}
              {isDemoMode ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200 mb-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Ready for the Full Experience?</h3>
                    <p className="text-gray-700 mb-4">Unlock all 4 practice exams with 240 questions for just €21.99</p>
                    <button
                      onClick={() => goToPayment(selectedExamType)}
                      className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-bold text-lg flex items-center justify-center gap-3 shadow-lg shadow-blue-500/30"
                    >
                      <CreditCard className="w-6 h-6" />
                      Get Full Access - €21.99
                    </button>
                  </div>
                  <button
                    onClick={handleBackToHome}
                    className="w-full py-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-semibold"
                  >
                    Return to Main Page
                  </button>
                </div>
              ) : (
                <div className="flex gap-4">
                  <button
                    onClick={handleBackToHome}
                    className="flex-1 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold shadow-lg shadow-blue-500/30"
                  >
                    Back to Home
                  </button>
                  <button
                    onClick={() => startExam(selectedExamType, selectedExamNumber)}
                    className="flex-1 py-4 bg-white text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-semibold border-2 border-gray-200"
                  >
                    Retake Exam
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Question Review with Filter */}
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Detailed Review</h3>
              
              {/* Filter Buttons */}
              <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
                <button
                  onClick={() => setResultsFilter('all')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    resultsFilter === 'all'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All ({currentQuestions.length})
                </button>
                <button
                  onClick={() => setResultsFilter('correct')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-1 ${
                    resultsFilter === 'correct'
                      ? 'bg-white text-emerald-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <CheckCircle className="w-4 h-4" />
                  Correct ({correctCount})
                </button>
                <button
                  onClick={() => setResultsFilter('incorrect')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-1 ${
                    resultsFilter === 'incorrect'
                      ? 'bg-white text-red-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <XCircle className="w-4 h-4" />
                  Incorrect ({incorrectCount})
                </button>
              </div>
            </div>

            {filteredQuestions.length === 0 ? (
              <div className="text-center py-12">
                <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No questions match the selected filter</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredQuestions.map(({ question, originalIndex }) => {
                  const userAnswer = userAnswers[originalIndex];
                  const isCorrect = userAnswer === question.correct;
                  const detailedResult = question.detailedResult; // May have explanation

                  return (
                    <div
                      key={originalIndex}
                      className="bg-white p-6 rounded-2xl border-2 border-gray-100 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`p-3 rounded-xl shrink-0 ${isCorrect ? 'bg-emerald-100' : 'bg-red-100'}`}>
                          {isCorrect ? (
                            <CheckCircle className="w-6 h-6 text-emerald-600" />
                          ) : (
                            <XCircle className="w-6 h-6 text-red-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="inline-block bg-gray-100 px-3 py-1 rounded-lg text-sm font-bold text-gray-700">
                              Question {originalIndex + 1}
                            </span>
                            {isCorrect ? (
                              <span className="inline-block bg-emerald-50 text-emerald-700 px-3 py-1 rounded-lg text-xs font-semibold">
                                ✓ Correct
                              </span>
                            ) : (
                              <span className="inline-block bg-red-50 text-red-700 px-3 py-1 rounded-lg text-xs font-semibold">
                                ✗ Incorrect
                              </span>
                            )}
                          </div>
                          <p className="text-gray-900 mb-4 leading-relaxed font-medium text-lg">
                            {question.question}
                          </p>

                          {/* ALL ANSWER OPTIONS WITH COLOR CODING */}
                          <div className="space-y-2 mb-4">
                            {question.options.map((option, optionIndex) => {
                              const optionLetter = String.fromCharCode(65 + optionIndex); // A, B, C, D
                              const isCorrectOption = optionIndex === question.correct;
                              const isUserOption = optionIndex === userAnswer;
                              
                              let optionClass = 'flex items-start gap-3 p-4 rounded-xl border-2 transition-all';
                              
                              if (isCorrectOption) {
                                // Correct answer - always green
                                optionClass += ' bg-emerald-50 border-emerald-300';
                              } else if (isUserOption && !isCorrect) {
                                // User's wrong answer - red
                                optionClass += ' bg-red-50 border-red-300';
                              } else {
                                // Other options - gray
                                optionClass += ' bg-gray-50 border-gray-200';
                              }

                              return (
                                <div key={optionIndex} className={optionClass}>
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold shrink-0 ${
                                    isCorrectOption
                                      ? 'bg-emerald-600 text-white'
                                      : isUserOption && !isCorrect
                                      ? 'bg-red-600 text-white'
                                      : 'bg-gray-200 text-gray-700'
                                  }`}>
                                    {optionLetter}
                                  </div>
                                  <div className="flex-1">
                                    <p className={`leading-relaxed ${
                                      isCorrectOption
                                        ? 'text-emerald-900 font-semibold'
                                        : isUserOption && !isCorrect
                                        ? 'text-red-900 font-semibold'
                                        : 'text-gray-700'
                                    }`}>
                                      {option}
                                    </p>
                                    {isCorrectOption && (
                                      <span className="inline-block mt-1 text-xs font-bold text-emerald-700">
                                        ✓ Correct Answer
                                      </span>
                                    )}
                                    {isUserOption && !isCorrect && (
                                      <span className="inline-block mt-1 text-xs font-bold text-red-700">
                                        ✗ Your Answer
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* EXPLANATION SECTION */}
                          {detailedResult?.explanation && (
  <div className="bg-emerald-50 p-4 rounded-xl border-l-4 border-emerald-500 mt-4">
    <h4 className="text-emerald-900 font-bold mb-2 flex items-center gap-2">
      <BookOpen className="w-4 h-4" />
      Explanation
    </h4>
    <p className="text-emerald-900 leading-relaxed">
      {detailedResult.explanation}
    </p>
  </div>
)}
                      
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /**
   * LOGIN SCREEN
   */
  const renderLogin = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-8">
      <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md border border-gray-100">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl mb-4">
            <User className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h2>
          <p className="text-gray-600">Log in to continue your exam preparation</p>
        </div>

        {formError && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
            <XCircle className="w-5 h-5 shrink-0" />
            <span>{formError}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-bold shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="w-5 h-5 animate-spin" />
                Logging in...
              </span>
            ) : (
              'Log In'
            )}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-gray-600">
            Don't have an account?{' '}
            <button
              onClick={() => {
                setCurrentScreen('register');
                setFormError('');
              }}
              className="text-blue-700 font-bold hover:text-blue-800 transition-colors"
            >
              Sign up
            </button>
          </p>
        </div>

        <button
          onClick={handleBackToHome}
          className="mt-6 w-full py-3 text-gray-600 hover:text-gray-900 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <Home className="w-4 h-4" />
          Back to Home
        </button>
      </div>
    </div>
  );

  /**
   * REGISTER SCREEN
   */
  const renderRegister = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-8">
      <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md border border-gray-100">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl mb-4">
            <Award className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h2>
          <p className="text-gray-600">Start your PRINCE2 certification journey</p>
        </div>

        {formError && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
            <XCircle className="w-5 h-5 shrink-0" />
            <span>{formError}</span>
          </div>
        )}

        <form onSubmit={handleRegister} className="space-y-5">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Full Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
              placeholder="John Doe"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
              placeholder="••••••••"
              required
              minLength="6"
            />
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Minimum 6 characters
            </p>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-bold shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="w-5 h-5 animate-spin" />
                Creating account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => {
                setCurrentScreen('login');
                setFormError('');
              }}
              className="text-blue-700 font-bold hover:text-blue-800 transition-colors"
            >
              Log in
            </button>
          </p>
        </div>

        <button
          onClick={handleBackToHome}
          className="mt-6 w-full py-3 text-gray-600 hover:text-gray-900 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <Home className="w-4 h-4" />
          Back to Home
        </button>
      </div>
    </div>
  );

  /**
   * PAYMENT SCREEN
   */
  const renderPayment = () => {
    if (!showPaymentModal) {
      setShowPaymentModal(true);
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md text-center border border-gray-100">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl mb-4">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Unlock Full Access</h2>
            <p className="text-gray-600">Complete your purchase to access all practice exams</p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8 border-2 border-blue-200">
            <div className="flex items-center justify-between mb-6">
              <span className="text-lg font-bold text-gray-900">Full Access Package</span>
              <div className="text-right">
                <p className="text-3xl font-bold text-blue-600">€21.99</p>
                <p className="text-xs text-gray-600">one-time payment</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-left">
              <li className="flex items-center gap-3 text-gray-700">
                <div className="bg-blue-100 p-1.5 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                </div>
                <span>4 Foundation Practice Exams (240 questions)</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <div className="bg-blue-100 p-1.5 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                </div>
                <span>4 Practitioner Practice Exams (240 questions)</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <div className="bg-blue-100 p-1.5 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                </div>
                <span>Instant results and detailed explanations</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <div className="bg-blue-100 p-1.5 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                </div>
                <span>Lifetime access to all exams</span>
              </li>
            </ul>
          </div>

          <button
            onClick={handleOpenPaymentModal}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-bold text-lg flex items-center justify-center gap-3 shadow-lg shadow-blue-500/30 mb-4"
          >
            <CreditCard className="w-6 h-6" />
            Complete Purchase - €21.99
          </button>

          <button
            onClick={handleBackToHome}
            className="w-full py-3 text-gray-600 hover:text-gray-900 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>
    );
  };

  /**
   * ACCOUNT SCREEN
   */
  const renderAccount = () => {
    const totalSpent = purchaseHistory.reduce((sum, purchase) => sum + purchase.amount, 0);

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent mb-2">
                Account Dashboard
              </h1>
              <p className="text-gray-600">Manage your profile and view your activity</p>
            </div>
            <button
              onClick={handleBackToHome}
              className="px-5 py-2.5 bg-white text-gray-700 rounded-xl hover:bg-gray-100 transition-colors flex items-center gap-2 font-medium shadow-sm"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </button>
          </div>

          {updateSuccess && (
            <div className="mb-6 p-4 bg-emerald-50 border-2 border-emerald-200 rounded-xl text-emerald-700 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 shrink-0" />
              <span className="font-medium">Profile updated successfully!</span>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {/* Account Overview Stats */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Account Status</p>
                  <p className="text-lg font-bold text-gray-900">Active</p>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Member Since</p>
                <p className="text-sm font-semibold text-gray-700">
                  {formatDate(user?.createdAt || new Date())}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-emerald-100 p-3 rounded-xl">
                  <BookOpen className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Courses Owned</p>
                  <p className="text-lg font-bold text-gray-900">
                    {(user?.hasPaidFoundation ? 1 : 0) + (user?.hasPaidPractitioner ? 1 : 0)} / 2
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <div className="flex gap-2">
                  {user?.hasPaidFoundation && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-semibold">
                      Foundation
                    </span>
                  )}
                  {user?.hasPaidPractitioner && (
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-semibold">
                      Practitioner
                    </span>
                  )}
                  {!user?.hasPaidFoundation && !user?.hasPaidPractitioner && (
                    <span className="text-xs text-gray-500">No courses yet</span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-100 p-3 rounded-xl">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Invested</p>
                  <p className="text-lg font-bold text-gray-900">
                    €{totalSpent.toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  {purchaseHistory.length} {purchaseHistory.length === 1 ? 'purchase' : 'purchases'}
                </p>
              </div>
            </div>
          </div>

          {/* Profile Information Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Profile Information</h2>
                <p className="text-gray-600 text-sm mt-1">Update your personal details</p>
              </div>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors font-medium border border-blue-200 flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Edit Profile
                </button>
              )}
            </div>

            {updateError && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                <XCircle className="w-5 h-5 shrink-0" />
                <span>{updateError}</span>
              </div>
            )}

            {!isEditing ? (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-600 mb-2">Full Name</label>
                  <div className="bg-gray-50 px-4 py-3 rounded-xl">
                    <p className="text-lg text-gray-900">{user?.name}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-600 mb-2">Email Address</label>
                  <div className="bg-gray-50 px-4 py-3 rounded-xl">
                    <p className="text-lg text-gray-900">{user?.email}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-600 mb-2">Account Type</label>
                  <div className="bg-gray-50 px-4 py-3 rounded-xl">
                    <p className="text-lg text-gray-900">Standard User</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-600 mb-2">Account ID</label>
                  <div className="bg-gray-50 px-4 py-3 rounded-xl">
                    <p className="text-lg text-gray-900 font-mono text-sm">
                      {user?.id || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpdateProfile} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className="flex-1 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-bold disabled:opacity-50 shadow-lg shadow-blue-500/30"
                  >
                    {isUpdating ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-bold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Purchase History */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-100 p-3 rounded-xl">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Purchase History</h2>
                <p className="text-gray-600 text-sm">Your course purchases and transactions</p>
              </div>
            </div>

            {loadingHistory ? (
              <div className="text-center py-12">
                <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading purchase history...</p>
              </div>
            ) : purchaseHistory.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-4">No purchases yet</p>
                <p className="text-gray-500 text-sm mb-6">
                  Start your PRINCE2 certification journey today
                </p>
                <button
                  onClick={handleBackToHome}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold shadow-lg shadow-blue-500/30"
                >
                  Browse Courses
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {purchaseHistory.map((purchase) => (
                  <div
                    key={purchase.id}
                    className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-xl hover:shadow-md transition-all border border-gray-200"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className={`p-3 rounded-xl ${
                          purchase.courseType === 'foundation' 
                            ? 'bg-blue-100' 
                            : 'bg-indigo-100'
                        }`}>
                          {purchase.courseType === 'foundation' ? (
                            <BookOpen className={`w-6 h-6 ${
                              purchase.courseType === 'foundation' 
                                ? 'text-blue-600' 
                                : 'text-indigo-600'
                            }`} />
                          ) : (
                            <Award className={`w-6 h-6 ${
                              purchase.courseType === 'foundation' 
                                ? 'text-blue-600' 
                                : 'text-indigo-600'
                            }`} />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">
                            {purchase.courseName}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2">
                            Purchased on {formatDate(purchase.purchaseDate)}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                              purchase.status === 'completed'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {purchase.status === 'completed' ? '✓ Completed' : 'Pending'}
                            </span>
                            <span className="text-xs text-gray-500">
                              Transaction ID: {purchase.id}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">
                          €{purchase.amount.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">{purchase.currency}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
{/* Exam Results History */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-indigo-100 p-3 rounded-xl">
                <Award className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Exam Results History</h2>
                <p className="text-gray-600 text-sm">Your recent exam attempts and scores</p>
              </div>
            </div>

            {/* Fetch and display exam history */}
            <ExamHistory userId={user?.id} token={token} />
          </div>

          {/* My Courses Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-emerald-100 p-3 rounded-xl">
                <BookOpen className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">My Courses</h2>
                <p className="text-gray-600 text-sm">Access and manage your course library</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Foundation */}
              <div className={`rounded-2xl p-6 border-2 transition-all ${
                user?.hasPaidFoundation 
                  ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-green-50' 
                  : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl ${user?.hasPaidFoundation ? 'bg-emerald-100' : 'bg-gray-200'}`}>
                      <BookOpen className={`w-6 h-6 ${user?.hasPaidFoundation ? 'text-emerald-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Foundation</h3>
                      <p className="text-sm text-gray-600">Entry Level</p>
                    </div>
                  </div>
                  {user?.hasPaidFoundation ? (
                    <CheckCircle className="w-7 h-7 text-emerald-600" />
                  ) : (
                    <XCircle className="w-7 h-7 text-gray-400" />
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">4 Practice Exams • 240 Questions</p>
                {user?.hasPaidFoundation ? (
                  <div className="space-y-3">
                    <div className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-xl text-sm font-bold inline-block">
                      ✓ Full Access
                    </div>
                    <button
                      onClick={() => {
                        setSelectedExamType('foundation');
                        setCurrentScreen('exam-select');
                      }}
                      className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-bold shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
                    >
                      Start Practice Exam
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => goToPayment('foundation')}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-bold shadow-lg shadow-blue-500/30"
                  >
                    Purchase - €21.99
                  </button>
                )}
              </div>

              {/* Practitioner */}
              <div className={`rounded-2xl p-6 border-2 transition-all ${
                user?.hasPaidPractitioner 
                  ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-green-50' 
                  : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl ${user?.hasPaidPractitioner ? 'bg-emerald-100' : 'bg-gray-200'}`}>
                      <Award className={`w-6 h-6 ${user?.hasPaidPractitioner ? 'text-emerald-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Practitioner</h3>
                      <p className="text-sm text-gray-600">Advanced Level</p>
                    </div>
                  </div>
                  {user?.hasPaidPractitioner ? (
                    <CheckCircle className="w-7 h-7 text-emerald-600" />
                  ) : (
                    <XCircle className="w-7 h-7 text-gray-400" />
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">4 Practice Exams • 240 Questions</p>
                {user?.hasPaidPractitioner ? (
                  <div className="space-y-3">
                    <div className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-xl text-sm font-bold inline-block">
                      ✓ Full Access
                    </div>
                    <button
                      onClick={() => {
                        setSelectedExamType('practitioner');
                        setCurrentScreen('exam-select');
                      }}
                      className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 text-sm font-bold shadow-lg shadow-purple-500/30 flex items-center justify-center gap-2"
                    >
                      Start Practice Exam
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => goToPayment('practitioner')}
                    className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 text-sm font-bold shadow-lg shadow-purple-500/30"
                  >
                    Purchase - €21.99
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * TERMS AND CONDITIONS SCREEN
   */
  const renderTerms = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-2 rounded-xl">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                ExamReadyPM
              </h1>
            </div>
            <button
              onClick={handleBackToHome}
              className="px-5 py-2.5 bg-white text-gray-700 rounded-xl hover:bg-gray-100 transition-colors flex items-center gap-2 font-medium shadow-sm border border-gray-200"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
          <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="prose prose-blue max-w-none space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Agreement to Terms</h2>
              <p>
                By accessing and using ExamReadyPM ("the Service"), you accept and agree to be bound by the terms and provisions of this agreement. 
                These Terms and Conditions constitute a legally binding agreement between you and ExamReadyPM Group, a company registered in the Netherlands.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Use of Service</h2>
              <p>
                ExamReadyPM provides online practice examination materials for PRINCE2 certification preparation. The Service is intended for personal, 
                non-commercial use only. You agree to use the Service only for lawful purposes and in accordance with these Terms.
              </p>
              <p className="mt-4">You agree not to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Share your account credentials with others</li>
                <li>Reproduce, distribute, or commercially exploit any content from the Service</li>
                <li>Use the Service in any way that could damage, disable, or impair the Service</li>
                <li>Attempt to gain unauthorized access to any portion of the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Account Registration</h2>
              <p>
                To access certain features of the Service, you must register for an account. You agree to provide accurate, current, and complete 
                information during registration and to update such information to keep it accurate, current, and complete. You are responsible for 
                safeguarding your password and for all activities that occur under your account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Payment and Pricing</h2>
              <p>
                Access to full practice examinations requires payment. All prices are stated in Euros (€) and are inclusive of applicable Dutch VAT. 
                Payment is processed securely through Stripe. By making a purchase, you authorize us to charge your payment method for the total amount 
                of your purchase.
              </p>
              <p className="mt-4">
                Course access is granted immediately upon successful payment and is valid for the lifetime of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Right of Withdrawal (EU Consumer Rights)</h2>
              <p>
                In accordance with EU Directive 2011/83/EU and Dutch consumer protection law, you have the right to withdraw from your purchase 
                within 14 days without giving any reason. However, this right of withdrawal does not apply to digital content if performance has begun 
                with your prior express consent and acknowledgment that you thereby lose your right of withdrawal.
              </p>
              <p className="mt-4">
                By accessing the purchased course content, you expressly consent to immediate performance and acknowledge that you lose your right of withdrawal.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Intellectual Property Rights</h2>
              <p>
                All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are owned by 
                ExamReadyPM Group and are protected by international copyright, trademark, and other intellectual property laws.
              </p>
              <p className="mt-4">
                PRINCE2® is a registered trademark of AXELOS Limited. ExamReadyPM is not affiliated with, endorsed by, or sponsored by AXELOS Limited.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Disclaimer of Warranties</h2>
              <p>
                The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. ExamReadyPM does not 
                guarantee that the Service will be uninterrupted, secure, or error-free. We do not warrant that the use of our practice materials 
                will result in passing the PRINCE2 certification examination.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by Dutch law, ExamReadyPM Group shall not be liable for any indirect, incidental, special, consequential, 
                or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, 
                or other intangible losses resulting from your use of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Governing Law and Jurisdiction</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the Netherlands. Any disputes arising from these Terms 
                or your use of the Service shall be subject to the exclusive jurisdiction of the courts of Amsterdam, the Netherlands.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page 
                and updating the "Last updated" date. Your continued use of the Service after such modifications constitutes your acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Contact Information</h2>
              <p>
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="font-semibold">ExamReadyPM Group</p>
                <p>Herengracht 208</p>
                <p>1016 BS Amsterdam</p>
                <p>The Netherlands</p>
                <p className="mt-2">Email: <a href="mailto:examreadypm@gmail.com" className="text-blue-600 hover:text-blue-700">examreadypm@gmail.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );

  /**
   * PRIVACY POLICY SCREEN
   */
  const renderPrivacy = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-2 rounded-xl">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                ExamReadyPM
              </h1>
            </div>
            <button
              onClick={handleBackToHome}
              className="px-5 py-2.5 bg-white text-gray-700 rounded-xl hover:bg-gray-100 transition-colors flex items-center gap-2 font-medium shadow-sm border border-gray-200"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="prose prose-blue max-w-none space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Introduction</h2>
              <p>
                ExamReadyPM Group ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, 
                disclose, and safeguard your information when you use our Service. This policy complies with the EU General Data Protection Regulation 
                (GDPR) and Dutch data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Data Controller</h2>
              <p>
                The data controller responsible for your personal data is:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="font-semibold">ExamReadyPM Group</p>
                <p>Herengracht 208</p>
                <p>1016 BS Amsterdam</p>
                <p>The Netherlands</p>
                <p className="mt-2">Email: <a href="mailto:examreadypm@gmail.com" className="text-blue-600 hover:text-blue-700">examreadypm@gmail.com</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Information We Collect</h2>
              
              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">3.1 Personal Information</h3>
              <p>We collect information that you provide directly to us, including:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Name and email address (when you create an account)</li>
                <li>Payment information (processed securely by Stripe)</li>
                <li>Account credentials (encrypted password)</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">3.2 Usage Information</h3>
              <p>We automatically collect certain information when you use our Service:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Exam attempts and scores</li>
                <li>Time spent on practice exams</li>
                <li>Course progress and completion status</li>
                <li>Device and browser information</li>
                <li>IP address and location data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Legal Basis for Processing (GDPR)</h2>
              <p>We process your personal data based on the following legal grounds:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Contract Performance:</strong> To provide the Service you purchased</li>
                <li><strong>Legitimate Interest:</strong> To improve our Service and prevent fraud</li>
                <li><strong>Legal Obligation:</strong> To comply with accounting and tax requirements</li>
                <li><strong>Consent:</strong> Where you have provided explicit consent for specific processing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Provide, maintain, and improve our Service</li>
                <li>Process your transactions and send related information</li>
                <li>Send you technical notices and support messages</li>
                <li>Track your progress and generate performance statistics</li>
                <li>Respond to your comments and questions</li>
                <li>Detect, prevent, and address technical issues and fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Data Sharing and Disclosure</h2>
              <p>We do not sell your personal data. We may share your information with:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Payment Processor:</strong> Stripe processes payments securely. See Stripe's privacy policy for details.</li>
                <li><strong>Service Providers:</strong> Third parties who provide hosting, analytics, and customer support services</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Data Retention</h2>
              <p>
                We retain your personal data for as long as necessary to provide our Service and comply with legal obligations. 
                Account information is retained until you request account deletion. Financial records are retained for 7 years in accordance 
                with Dutch accounting requirements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Your Rights Under GDPR</h2>
              <p>As an EU resident, you have the following rights:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Right to Object:</strong> Object to certain types of processing</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us at <a href="mailto:examreadypm@gmail.com" className="text-blue-600 hover:text-blue-700">examreadypm@gmail.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, 
                alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. International Data Transfers</h2>
              <p>
                Your data is primarily stored within the EU. If data is transferred outside the EU, we ensure appropriate safeguards are in place, 
                such as Standard Contractual Clauses approved by the European Commission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Cookies and Tracking</h2>
              <p>
                We use essential cookies to provide our Service. These include authentication tokens to keep you logged in. 
                We do not use advertising or tracking cookies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Children's Privacy</h2>
              <p>
                Our Service is not intended for individuals under 16 years of age. We do not knowingly collect personal information from children under 16.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy 
                on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Contact Us and Complaints</h2>
              <p>
                If you have questions or complaints about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p>Email: <a href="mailto:examreadypm@gmail.com" className="text-blue-600 hover:text-blue-700">examreadypm@gmail.com</a></p>
              </div>
              <p className="mt-4">
                You also have the right to lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens):
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="font-semibold">Autoriteit Persoonsgegevens</p>
                <p>Postbus 93374</p>
                <p>2509 AJ Den Haag</p>
                <p>The Netherlands</p>
                <p className="mt-2">Website: <a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">autoriteitpersoonsgegevens.nl</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );

  /**
   * COMPLAINT POLICY SCREEN
   */
  const renderComplaints = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-2 rounded-xl">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                ExamReadyPM
              </h1>
            </div>
            <button
              onClick={handleBackToHome}
              className="px-5 py-2.5 bg-white text-gray-700 rounded-xl hover:bg-gray-100 transition-colors flex items-center gap-2 font-medium shadow-sm border border-gray-200"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Complaint Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="prose prose-blue max-w-none space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Our Commitment</h2>
              <p>
                ExamReadyPM Group is committed to providing high-quality service to all our users. We value feedback and take all complaints seriously. 
                This Complaint Policy outlines how you can raise concerns and how we will handle them, in accordance with Dutch consumer protection law 
                and EU regulations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. What Constitutes a Complaint</h2>
              <p>A complaint may relate to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Quality or accuracy of practice examination content</li>
                <li>Technical issues or service disruptions</li>
                <li>Billing or payment concerns</li>
                <li>Customer service experiences</li>
                <li>Privacy or data protection concerns</li>
                <li>Any other aspect of our service that did not meet your expectations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. How to Submit a Complaint</h2>
              
              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">3.1 Initial Contact</h3>
              <p>Please submit your complaint in writing via email to:</p>
              <div className="mt-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                <p className="font-semibold text-blue-900">Email: <a href="mailto:examreadypm@gmail.com" className="text-blue-600 hover:text-blue-700">examreadypm@gmail.com</a></p>
                <p className="text-sm text-gray-600 mt-2">Subject line: "Complaint - [Brief description]"</p>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">3.2 Information to Include</h3>
              <p>To help us resolve your complaint efficiently, please provide:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Your name and contact details</li>
                <li>Your account email address (if applicable)</li>
                <li>Date and time of the incident (if applicable)</li>
                <li>Detailed description of the issue</li>
                <li>Any relevant screenshots or documentation</li>
                <li>What outcome you are seeking</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Our Complaint Handling Process</h2>
              
              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 1: Acknowledgment</h3>
              <p>
                We will acknowledge receipt of your complaint within <strong>2 business days</strong> and provide you with a reference number 
                for tracking purposes.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 2: Investigation</h3>
              <p>
                We will thoroughly investigate your complaint, which may involve:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Reviewing relevant records and documentation</li>
                <li>Consulting with relevant team members</li>
                <li>Requesting additional information from you if needed</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 3: Resolution</h3>
              <p>
                We aim to provide a substantive response to your complaint within <strong>14 calendar days</strong> of receipt. 
                If your complaint requires more time to investigate, we will inform you of the delay and provide an estimated resolution date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Possible Outcomes</h2>
              <p>Depending on the nature of your complaint, we may:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Correct any errors or inaccuracies in our content</li>
                <li>Provide a refund or credit where appropriate</li>
                <li>Offer additional support or resources</li>
                <li>Implement system improvements to prevent future occurrences</li>
                <li>Provide an explanation if we find no error occurred</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. If You Are Not Satisfied</h2>
              <p>
                If you are not satisfied with our response, you may:
              </p>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">6.1 Request Escalation</h3>
              <p>
                Ask for your complaint to be reviewed by a senior member of our team. Please reply to our response email with "Escalation Request" 
                in the subject line.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">6.2 Alternative Dispute Resolution</h3>
              <p>
                You may contact an independent alternative dispute resolution (ADR) provider. In the Netherlands, this includes:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="font-semibold">Stichting WebwinkelKeur</p>
                <p>Dispute Committee (Geschillencommissie)</p>
                <p>Website: <a href="https://www.sgc.nl" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">www.sgc.nl</a></p>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">6.3 EU Online Dispute Resolution Platform</h3>
              <p>
                EU residents can also use the European Commission's Online Dispute Resolution platform:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p>Website: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">ec.europa.eu/consumers/odr</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Consumer Rights (Netherlands/EU)</h2>
              <p>
                This Complaint Policy does not affect your statutory rights as a consumer under Dutch and EU law, including:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Right to clear information before purchase</li>
                <li>Right to conforming goods and services</li>
                <li>Right to withdraw from distance sales (subject to digital content exceptions)</li>
                <li>Right to remedies for faulty goods or services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Learning from Complaints</h2>
              <p>
                We view complaints as opportunities to improve. We regularly review complaint trends and outcomes to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Identify systemic issues</li>
                <li>Improve our service quality</li>
                <li>Enhance our policies and procedures</li>
                <li>Train our team on best practices</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Confidentiality</h2>
              <p>
                All complaints are treated confidentially. Information will only be shared with team members directly involved in resolving your complaint 
                or as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Contact Information</h2>
              <p>
                For any questions about this Complaint Policy or to submit a complaint:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="font-semibold">ExamReadyPM Group</p>
                <p>Herengracht 208</p>
                <p>1016 BS Amsterdam</p>
                <p>The Netherlands</p>
                <p className="mt-2">Email: <a href="mailto:examreadypm@gmail.com" className="text-blue-600 hover:text-blue-700">examreadypm@gmail.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <div className="App">
      {currentScreen === 'home' && renderHome()}
      {currentScreen === 'exam-select' && renderExamSelect()}
      {currentScreen === 'exam' && renderExam()}
      {currentScreen === 'results' && renderResults()}
      {currentScreen === 'login' && renderLogin()}
      {currentScreen === 'register' && renderRegister()}
      {currentScreen === 'payment' && renderPayment()}
      {currentScreen === 'account' && renderAccount()}
      {currentScreen === 'terms' && renderTerms()}
      {currentScreen === 'privacy' && renderPrivacy()}
      {currentScreen === 'complaints' && renderComplaints()}
      
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setPurchasingCourseType(null);
        }}
        onSuccess={handlePaymentSuccess}
        courseType={purchasingCourseType || 'foundation'}
      />
    </div>
  );
}

export default App;